'use client'

import React, { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { Menu, X, ChevronDown, ChevronRight, ShoppingBag, Tag, Sparkles, Store, ArrowUpRight, Flame, Layers, Home, Calendar } from 'lucide-react'
import { CategoryData, EventResponse, Merchant, Promotion } from '@/services/dataTypes'
import { getBaseImageUrl, getEventsHref, getMerchantHref, getPromotionHref } from '@/constants/hooks'

interface Props {
    unique_id: string
    merchantData: Merchant[]
    categories: CategoryData[] | any
    headerPromoMerchant: Merchant[] | null
    companyDomain: string
    mer_slug: string
    mer_slug_type: string
    promo_slug: string
    cat_slug: string
    blog_url?: string
    blog_title?: string
    company_logo: string | null
    company_name?: string
    events: EventResponse[]
    promotions: Promotion[]
    productMerchants?: any[]
}

const Navbar = ({ unique_id, merchantData, categories, companyDomain, mer_slug, mer_slug_type, cat_slug, blog_url, blog_title, company_logo, company_name, events, promotions, promo_slug }: Props) => {
    const [mobileOpen, setMobileOpen] = useState(false)
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
    const [mobileDropdown, setMobileDropdown] = useState<string | null>(null)
    const dropdownTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [scrolled, setScrolled] = useState(false)
    const [fixed, setFixed] = useState(false)
    const lastScrollY = useRef(0)
    const pathname = usePathname()
    const effectiveDomain = (!companyDomain || companyDomain === 'no domain in cookie' || companyDomain.startsWith('localhost')) ? '' : companyDomain
    const logo = getBaseImageUrl(effectiveDomain, company_logo, '/themes/Theme_3/images/logo.png')

    useEffect(() => {
        const handleScroll = () => {
            const currentY = window.scrollY
            const diff = currentY - lastScrollY.current

            setScrolled(currentY > 10)

            if (diff < -5) {
                // scrolling up — fix navbar at top
                setFixed(true)
            } else if (diff > 5 && currentY > 80) {
                // scrolling down past threshold — unfix (scroll away)
                setFixed(false)
            }

            if (currentY <= 0) setFixed(false)

            lastScrollY.current = currentY
        }
        window.addEventListener('scroll', handleScroll, { passive: true })
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const navItems = [
        { name: 'Home', href: '/', icon: Home, hasDropdown: false },
        { name: 'Categories', href: '/category', icon: Tag, hasDropdown: true },
        { name: 'Stores', href: '/all-stores', icon: Store, hasDropdown: true },
        { name: 'Products', href: '/all-products', icon: ShoppingBag, hasDropdown: true },
        ...(Array.isArray(promotions) && promotions.length > 0 ? [{ name: 'Promotions', href: `/${promo_slug}`, icon: Flame, hasDropdown: true }] : []),
        ...(Array.isArray(events) && events.length > 0 ? [{ name: 'Events', href: '/events', icon: Calendar, hasDropdown: true }] : []),
        ...(blog_title && blog_url ? [{ name: blog_title, href: blog_url, hasDropdown: false, isExternal: /^https?:\/\//i.test(blog_url) }] : []),
    ]

    const isActive = (href: string) => href === '/' ? pathname === '/' : (pathname === href || pathname.startsWith(href.replace('/A', '')))

    return (
        <>
            {fixed && <div className="h-16" />}
            <header className={`${fixed ? 'fixed top-0 left-0 right-0 shadow-lg bg-[#071822]/95 backdrop-blur-md' : 'relative bg-[#071822] shadow-sm'
                } z-50 border-b border-white/10 transition-transform duration-200`}>
                <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center h-16 gap-6 justify-between">

                        {/* Logo */}
                        <Link href="/" className="flex-shrink-0 transition-transform hover:scale-105 duration-300">
                            {company_logo
                                ? <Image src={logo} alt={company_name || "logo"} width={240} height={64} priority unoptimized className="h-8 sm:h-9 w-auto object-contain" />
                                : <span className="text-xl font-extrabold text-white tracking-tight">{company_name || ''}</span>
                            }
                        </Link>

                        {/* Desktop Navigation */}
                        <nav className="hidden lg:flex items-center gap-1.5 ml-auto">
                            {navItems.map(item => (
                                <div key={item.name} className="relative"
                                    onMouseEnter={() => {
                                        if (!item.hasDropdown) return
                                        if (dropdownTimer.current) clearTimeout(dropdownTimer.current)
                                        dropdownTimer.current = setTimeout(() => setActiveDropdown(item.name), 150)
                                    }}
                                    onMouseLeave={() => {
                                        if (dropdownTimer.current) clearTimeout(dropdownTimer.current)
                                        dropdownTimer.current = setTimeout(() => setActiveDropdown(null), 150)
                                    }}
                                >
                                    {item.hasDropdown ? (
                                        <button className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer ${isActive(item.href) ? 'text-[#00d69f] bg-white/[0.08]' : 'text-slate-200 hover:text-[#00d69f] hover:bg-white/[0.05]'
                                            }`}>
                                            {item.icon && <item.icon size={15} className={isActive(item.href) ? 'text-[#00d69f]' : 'text-slate-400'} />}
                                            <span>{item.name}</span>
                                            <ChevronDown size={13} className={`transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180 text-[#00d69f]' : 'text-slate-400'}`} />
                                        </button>
                                    ) : (
                                        <Link
                                            href={item.href}
                                            {...(item.isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium transition-all duration-200 no-underline ${isActive(item.href) ? 'text-[#00d69f] bg-white/[0.08]' : 'text-slate-200 hover:text-[#00d69f] hover:bg-white/[0.05]'
                                                }`}
                                        >
                                            {item.icon && <item.icon size={15} className={isActive(item.href) ? 'text-[#00d69f]' : 'text-slate-400'} />}
                                            <span>{item.name}</span>
                                        </Link>
                                    )}

                                    {/* Clean, Simple Dropdown Menu */}
                                    {item.hasDropdown && activeDropdown === item.name && (
                                        <div className="absolute top-full left-0 mt-2 w-60 bg-[#0c1f2b] border border-white/10 rounded-2xl shadow-[0_16px_40px_rgba(0,0,0,0.5)] p-2 z-50 animate-fade-in">
                                            {/* Categories List */}
                                            {item.name === 'Categories' && (
                                                <div className="space-y-0.5 max-h-[280px] overflow-y-auto">
                                                    {categories?.slice(0, 8).map((cat: any, i: number) => (
                                                        <Link
                                                             key={i}
                                                            href={`/${cat?.url}`}
                                                            className="flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium text-slate-300 hover:text-[#00d69f] hover:bg-white/[0.06] transition-colors no-underline group"
                                                        >
                                                            <span className="truncate">{cat.name}</span>
                                                            <ChevronRight size={12} className="text-[#00d69f] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                                        </Link>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Stores List */}
                                            {item.name === 'Stores' && (
                                                <div className="space-y-0.5 max-h-[280px] overflow-y-auto">
                                                    {merchantData?.slice(0, 8).map((m: any, i: number) => (
                                                        <Link
                                                            key={i}
                                                            href={getMerchantHref(m, mer_slug, mer_slug_type)}
                                                            className="flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium text-slate-300 hover:text-[#00d69f] hover:bg-white/[0.06] transition-colors no-underline group"
                                                        >
                                                            <span className="truncate">{m.merchant_name}</span>
                                                            <ChevronRight size={12} className="text-[#00d69f] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                                        </Link>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Products List */}
                                            {item.name === 'Products' && (
                                                <div className="space-y-1 p-0.5">
                                                    <Link
                                                        href="/products"
                                                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-medium text-slate-300 hover:text-[#00d69f] hover:bg-white/[0.06] transition-colors no-underline"
                                                    >
                                                        <Sparkles size={14} className="text-[#00d69f]" />
                                                        <span>Brand Deals & Offers</span>
                                                    </Link>
                                                </div>
                                            )}

                                            {/* Promotions List */}
                                            {item.name === 'Promotions' && (
                                                <div className="space-y-0.5 max-h-[280px] overflow-y-auto">
                                                    {promotions?.slice(0, 8).map((p: any, i: number) => (
                                                        <Link
                                                            key={i}
                                                            href={getPromotionHref(p, promo_slug)}
                                                            className="flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium text-slate-300 hover:text-[#00d69f] hover:bg-white/[0.06] transition-colors no-underline group"
                                                        >
                                                            <span className="truncate">{p.name}</span>
                                                            <ChevronRight size={12} className="text-[#00d69f] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                                        </Link>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Events List */}
                                            {item.name === 'Events' && (
                                                <div className="space-y-0.5 max-h-[280px] overflow-y-auto">
                                                    {events?.slice(0, 8).map((e: any, i: number) => (
                                                        <Link
                                                            key={i}
                                                            href={getEventsHref(e, mer_slug_type)}
                                                            className="flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium text-slate-300 hover:text-[#00d69f] hover:bg-white/[0.06] transition-colors no-underline group"
                                                        >
                                                            <span className="truncate">{e.name}</span>
                                                            <ChevronRight size={12} className="text-[#00d69f] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                                        </Link>
                                                    ))}
                                                </div>
                                            )}

                                            {/* View All Footer */}
                                            <div className="pt-1.5 mt-1 border-t border-white/10 px-1">
                                                <Link
                                                    href={item.href}
                                                    className="flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-bold text-[#00d69f] hover:bg-[#00d69f]/10 transition-colors"
                                                >
                                                    <span>View All {item.name}</span>
                                                    <ArrowUpRight size={13} />
                                                </Link>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </nav>

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setMobileOpen(!mobileOpen)}
                            className="lg:hidden ml-auto p-2 text-slate-200 rounded-xl hover:bg-white/10 transition-all duration-300"
                        >
                            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {mobileOpen && (
                    <div className="lg:hidden bg-[#071822] border-t border-white/10 px-5 py-4 space-y-2 max-h-[calc(100vh-64px)] overflow-y-auto animate-slide-down shadow-xl">
                        {navItems.map(item => (
                            <div key={item.name}>
                                {item.hasDropdown ? (
                                    <button
                                        onClick={() => setMobileDropdown(mobileDropdown === item.name ? null : item.name)}
                                        className={`flex items-center justify-between w-full py-3 text-sm font-bold border-b border-white/10 transition-colors ${mobileDropdown === item.name ? 'text-[#00d69f]' : 'text-slate-200 hover:text-[#00d69f]'
                                            }`}
                                    >
                                        <span className="flex items-center gap-3">
                                            {item.icon && <item.icon size={16} className="text-[#00d69f]" />}
                                            {item.name}
                                        </span>
                                        <ChevronDown size={14} className={`transition-transform duration-200 ${mobileDropdown === item.name ? 'rotate-180 text-[#00d69f]' : 'text-slate-400'}`} />
                                    </button>
                                ) : (
                                    <Link
                                        href={item.href}
                                        {...(item.isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                        onClick={() => setMobileOpen(false)}
                                        className={`flex items-center gap-3 py-3 text-sm font-bold border-b border-white/10 transition-colors ${isActive(item.href) ? 'text-[#00d69f]' : 'text-slate-200 hover:text-[#00d69f]'}`}
                                    >
                                        {item.icon && <item.icon size={16} className="text-[#00d69f]" />}
                                        {item.name}
                                    </Link>
                                )}

                                {/* Mobile Dropdown */}
                                {item.hasDropdown && mobileDropdown === item.name && (
                                    <div className="bg-[#0a1c26] rounded-2xl border border-white/10 my-2 py-1 overflow-hidden">
                                        <div className="px-4 py-2 border-b border-white/10">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Popular {item.name}</p>
                                        </div>
                                        {item.name === 'Categories' && categories?.slice(0, 8).map((cat: any, i: number) => (
                                            <Link key={i} href={`/${cat?.url}`}
                                                onClick={() => setMobileOpen(false)}
                                                className="block px-4 py-2 text-xs font-semibold text-slate-200 hover:bg-white/5 hover:text-[#00d69f]">
                                                {cat.name}
                                            </Link>
                                        ))}
                                        {item.name === 'Stores' && merchantData?.slice(0, 8).map((m, i) => (
                                            <Link key={i} href={getMerchantHref(m, mer_slug, mer_slug_type)}
                                                onClick={() => setMobileOpen(false)}
                                                className="block px-4 py-2 text-xs font-semibold text-slate-200 hover:bg-white/5 hover:text-[#00d69f]">
                                                {m.merchant_name}
                                            </Link>
                                        ))}
                                        {item.name === 'Products' && (
                                            <div className="space-y-1 p-1">
                                                <Link href="/products"
                                                    onClick={() => setMobileOpen(false)}
                                                    className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-slate-200 hover:bg-white/5 hover:text-[#00d69f] transition-colors rounded-xl">
                                                    <Sparkles size={13} className="text-[#00d69f]" />
                                                    Brand Deals & Offers
                                                </Link>
                                            </div>
                                        )}
                                        {item.name === 'Promotions' && promotions?.slice(0, 8).map((p, i) => (
                                            <Link key={i} href={getPromotionHref(p, promo_slug)}
                                                onClick={() => setMobileOpen(false)}
                                                className="block px-4 py-2 text-xs font-semibold text-slate-200 hover:bg-white/5 hover:text-[#00d69f]">
                                                {p.name}
                                            </Link>
                                        ))}
                                        {item.name === 'Events' && events?.slice(0, 8).map((e, i) => (
                                            <Link key={i} href={getEventsHref(e, mer_slug_type)}
                                                onClick={() => setMobileOpen(false)}
                                                className="block px-4 py-2 text-xs font-semibold text-slate-200 hover:bg-white/5 hover:text-[#00d69f]">
                                                {e.name}
                                            </Link>
                                        ))}
                                        <div className="px-4 py-2 border-t border-white/10">
                                            <Link href={item.href}
                                                onClick={() => setMobileOpen(false)}
                                                className="text-xs font-bold text-[#00d69f] hover:underline inline-flex items-center gap-1">
                                                <span>View All {item.name}</span>
                                                <ArrowUpRight size={11} />
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <style dangerouslySetInnerHTML={{
                    __html: `
                    @keyframes fade-in {
                        from { opacity: 0; transform: translateY(-8px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes slide-down {
                        from { opacity: 0; transform: translateY(-12px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .animate-fade-in { animation: fade-in 0.2s ease-out; }
                    .animate-slide-down { animation: slide-down 0.25s ease-out; }
                `
                }} />
            </header>
        </>
    )
}

export default Navbar
